import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Package, Images, Upload } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import DataTable from "@/components/app/DataTable";
import Modal from "@/components/app/Modal";
import CsvImportModal from "@/components/app/CsvImportModal";
import ImageGallery from "@/components/app/ImageGallery";
import { Field, CheckField, useValidatedForm, isBlank, toNum } from "@/components/app/FormKit";
import AttributesEditor from "./AttributesEditor";
import VariantsEditor from "./VariantsEditor";
import { Link } from "react-router-dom";
import { toFormVariants, toApiVariants, fromLegacy, typeError, variantErrors, MAX_VARIANTS } from "@/lib/variants";
import { attributeErrors, toApiRows, toFormRows } from "@/lib/attributes";
import { btnOutline, btnPrimary, iconBtn, inr, pageSub, pageTitle } from "@/components/app/ui";

const empty = {
  name: "", description: "", price: "", compare_at_price: "", cost_price: "", sku: "", barcode: "", brand: "",
  tax_rate: "0", tax_inclusive: false, weight: "", stock: "0", min_stock: "5", category: "", subcategory: "", images: [], status: "active",
  featured: false, variants: [], attributes: [], option_types: [], variant_rows: [],
};
const STATUS_OPTS = [{ value: "active", label: "Active" }, { value: "draft", label: "Draft" }, { value: "archived", label: "Archived" }];
const stockState = (p) => (p.stock <= 0 ? "out" : p.stock <= (p.min_stock ?? 5) ? "low" : "in");

export default function Products() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [taxRows, setTaxRows] = useState([]);          // the store's per-category tax (Setup → Tax)
  const loadTax = useCallback(() => api.get("/vendor/tax").then(({ data }) => setTaxRows(data.rows)).catch(() => {}), []);
  const ruleFor = useCallback((cat) => { const r = taxRows.find((x) => x.category.toLowerCase() === (cat || "").trim().toLowerCase()); return r && r.type && r.rate != null ? r : null; }, [taxRows]);

  const load = useCallback(() => api.get("/vendor/products").then(({ data }) => setItems(data)).finally(() => setLoading(false)), []);
  useEffect(() => { load(); }, [load]);

  const validate = useCallback((f) => {
    const e = {};
    if (isBlank(f.name)) e.name = "Product name is required.";
    else if (f.name.trim().length < 2) e.name = "Name must be at least 2 characters.";
    const price = toNum(f.price);
    const withVariants = f.option_types.length > 0;
    if (withVariants) {
      const msg = typeError(f.option_types) || (!f.variant_rows.length ? "Add at least one variant." : "")
        || (f.variant_rows.length > MAX_VARIANTS ? `At most ${MAX_VARIANTS} variants.` : "")
        || (Object.keys(variantErrors(f.option_types, f.variant_rows)).length ? "Fix the highlighted variants." : "");
      if (msg) e.variants = msg;
    } else if (isBlank(f.price)) e.price = "Selling price is required.";
    else if (!(price > 0)) e.price = "Price must be greater than 0.";
    if (!withVariants && !isBlank(f.compare_at_price)) {
      const c = toNum(f.compare_at_price);
      if (!(c > 0)) e.compare_at_price = "Must be greater than 0.";
      else if (price > 0 && c <= price) e.compare_at_price = "Must be higher than the selling price.";
    }
    if (!isBlank(f.cost_price) && !(toNum(f.cost_price) >= 0)) e.cost_price = "Cost cannot be negative.";
    if (!withVariants && isBlank(f.stock)) e.stock = "Stock is required.";
    else if (!withVariants && (!Number.isInteger(toNum(f.stock)) || toNum(f.stock) < 0)) e.stock = "Whole number, 0 or more.";
    if (!isBlank(f.min_stock) && (!Number.isInteger(toNum(f.min_stock)) || toNum(f.min_stock) < 0)) e.min_stock = "Whole number, 0 or more.";
    if (!isBlank(f.category) && !ruleFor(f.category)) e.tax_rule = `No tax is set for “${f.category.trim()}”. Set it in Setup → Tax first.`;
    if (!isBlank(f.weight) && !(toNum(f.weight) >= 0)) e.weight = "Weight cannot be negative.";
    if (!isBlank(f.sku)) {
      const clash = items.find((p) => p.id !== editId && (p.sku || "").toLowerCase() === f.sku.trim().toLowerCase());
      if (clash) e.sku = `SKU already used by “${clash.name}”.`;
    }
    if (isBlank(f.category)) e.category = "Choose or enter a category.";
    if (Object.keys(attributeErrors(f.attributes)).length) e.attributes = "Fix the highlighted attribute rows.";
    return e;
  }, [items, editId, ruleFor]);

  const form = useValidatedForm(empty, validate);
  const { values: v, bind, set } = form;
  const hasVar = v.option_types.length > 0;
  const priced = v.variant_rows.map((r) => Number(r.price)).filter((n) => n > 0);
  const derivedPrice = priced.length ? String(Math.min(...priced)) : "";
  const derivedStock = String(v.variant_rows.reduce((s, r) => s + (Number(r.stock) || 0), 0));

  const openNew = () => { loadTax(); form.reset(empty); setEditId(null); setOpen(true); };
  const openEdit = (p) => {
    const imgs = p.images?.length ? p.images : p.image ? [p.image] : [];
    form.reset({
      ...empty, ...p, images: imgs,
      price: String(p.price ?? ""), compare_at_price: p.compare_at_price ? String(p.compare_at_price) : "", cost_price: p.cost_price ? String(p.cost_price) : "",
      tax_rate: String(p.tax_rate ?? 0), weight: p.weight ? String(p.weight) : "", stock: String(p.stock ?? 0), min_stock: String(p.min_stock ?? 5),
      sku: p.sku || "", barcode: p.barcode || "", brand: p.brand || "", category: p.category || "", subcategory: p.subcategory || "",
      description: p.description || "", status: p.status || "active", featured: !!p.featured, tax_inclusive: !!p.tax_inclusive,
      attributes: toFormRows(p.attributes),
      ...(() => { const fv = toFormVariants(p); return { option_types: fv.types, variant_rows: fv.rows }; })(),
    });
    setEditId(p.id); loadTax(); setOpen(true);
  };

  const taxRule = ruleFor(v.category);
  const save = async () => {
    if (v.category.trim() && !taxRule) { toast.error(`Set the tax for “${v.category.trim()}” in Setup → Tax first, then save this product.`); return; }
    if (!form.check()) return;
    setSaving(true);
    const payload = {
      ...v, name: v.name.trim(), price: parseFloat(v.price),
      compare_at_price: v.compare_at_price ? parseFloat(v.compare_at_price) : null,
      cost_price: v.cost_price ? parseFloat(v.cost_price) : null,
      tax_rate: taxRule ? taxRule.rate : 0, tax_inclusive: !!v.tax_inclusive, weight: v.weight ? parseFloat(v.weight) : null,
      stock: parseInt(v.stock, 10) || 0, min_stock: parseInt(v.min_stock, 10) || 5,
      image: v.images[0] || "", images: v.images, attributes: toApiRows(v.attributes),
      ...(v.option_types.length ? toApiVariants(v.option_types, v.variant_rows) : { option_types: [], variant_items: [] }),
    };
    delete payload.variant_rows;
    if (v.option_types.length) { payload.price = Math.min(...v.variant_rows.map((r) => Number(r.price))); payload.compare_at_price = null; payload.stock = v.variant_rows.reduce((s, r) => s + (Number(r.stock) || 0), 0); }
    try {
      if (editId) { await api.put(`/vendor/products/${editId}`, payload); toast.success(`“${payload.name}” updated successfully.`); }
      else { await api.post("/vendor/products", payload); toast.success(`“${payload.name}” added to your catalog.`); }
      setOpen(false); load();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete “${p.name}”? This cannot be undone.`)) return;
    try { await api.delete(`/vendor/products/${p.id}`); toast.success(`“${p.name}” deleted.`); load(); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  const columns = useMemo(() => [
    { key: "name", header: "Product", cell: (p) => (
      <div className="flex items-center gap-2.5 min-w-[200px]">
        <div className="w-9 h-9 rounded-md bg-slate-100 border border-slate-200 overflow-hidden shrink-0 flex items-center justify-center text-slate-400">
          {p.image ? <img src={p.image} alt="" className="w-full h-full object-cover" /> : <Package size={15} />}
        </div>
        <div className="min-w-0">
          <div className="font-semibold text-neutral-900 truncate flex items-center gap-1.5">{p.name}
            {p.featured && <span className="text-[10px] font-bold bg-orange-50 text-orange-600 border border-orange-200 px-1.5 rounded">Featured</span>}
          </div>
          <div className="text-[11px] text-slate-500 font-mono">{p.sku || "—"}</div>
        </div>
      </div>
    ) },
    { key: "category", header: "Category", hideBelow: "md", accessor: (p) => p.category || "", cell: (p) => <span className="text-slate-600">{p.category || "Uncategorized"}</span> },
    { key: "price", header: "Price", accessor: (p) => p.price, cell: (p) => (
      <span className="whitespace-nowrap">{p.variant_items?.length > 1 && new Set(p.variant_items.map((x) => x.price)).size > 1 && <span className="text-[11px] text-slate-400 mr-1">From</span>}<span className="font-semibold text-neutral-900">{inr(p.price)}</span>
        {p.compare_at_price ? <span className="text-[11px] text-slate-400 line-through ml-1.5">{inr(p.compare_at_price)}</span> : null}</span>
    ) },
    { key: "stock", header: "Stock", accessor: (p) => p.stock, cell: (p) => {
      const s = stockState(p);
      return <span className={`font-semibold ${s === "out" ? "text-red-600" : s === "low" ? "text-orange-600" : "text-green-600"}`}>{p.stock}</span>;
    } },
    { key: "variants", header: "Variants", hideBelow: "lg", accessor: (p) => p.variant_items?.length || 0, cell: (p) => (
      <span className="text-slate-500">{p.variant_items?.length || "—"}</span>
    ) },
    { key: "attributes", header: "Attributes", hideBelow: "lg", accessor: (p) => p.attributes?.length || 0, cell: (p) => (
      <span className="text-slate-500">{p.attributes?.length || "—"}</span>
    ) },
    { key: "images", header: "Images", hideBelow: "lg", accessor: (p) => (p.images?.length || (p.image ? 1 : 0)), cell: (p) => (
      <span className="inline-flex items-center gap-1 text-slate-500"><Images size={13} />{p.images?.length || (p.image ? 1 : 0)}</span>
    ) },
    { key: "status", header: "Status", accessor: (p) => p.status || "active", cell: (p) => (
      <span className={`inline-block text-[11px] px-2 py-0.5 rounded-md border font-medium capitalize ${(p.status || "active") === "active" ? "bg-green-50 text-green-700 border-green-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>{p.status || "active"}</span>
    ) },
    { key: "actions", header: "", sortable: false, align: "right", cell: (p) => (
      <div className="flex justify-end gap-0.5" onClick={(e) => e.stopPropagation()}>
        <button onClick={() => openEdit(p)} data-testid={`product-edit-${p.id}`} className={`${iconBtn} hover:bg-blue-50 hover:text-blue-600`} aria-label={`Edit ${p.name}`}><Pencil size={14} /></button>
        <button onClick={() => del(p)} data-testid={`product-delete-${p.id}`} className={`${iconBtn} hover:bg-red-50 hover:text-red-600`} aria-label={`Delete ${p.name}`}><Trash2 size={14} /></button>
      </div>
    ) },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], []);

  const filters = useMemo(() => [
    { key: "status", label: "All status", accessor: (p) => p.status || "active", options: STATUS_OPTS },
    { key: "category", label: "All categories", accessor: (p) => p.category || "" },
    { key: "stock", label: "Any stock", accessor: stockState, options: [{ value: "in", label: "In stock" }, { value: "low", label: "Low stock" }, { value: "out", label: "Out of stock" }] },
    { key: "featured", label: "Featured: any", accessor: (p) => (p.featured ? "yes" : "no"), options: [{ value: "yes", label: "Featured" }, { value: "no", label: "Not featured" }] },
  ], []);

  const categoryOptions = useMemo(() => [...new Set(items.map((p) => p.category).filter(Boolean))], [items]);

  return (
    <div className="space-y-4" data-testid="vendor-products">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className={pageTitle}>Products</h1>
          <p className={pageSub}>{items.length} products in your catalog.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setImportOpen(true)} data-testid="vendor-import-products-btn" className={btnOutline}><Upload size={15} /> Import CSV</button>
          <button onClick={openNew} data-testid="vendor-add-product-btn" className={btnPrimary}><Plus size={15} /> Add product</button>
        </div>
      </div>
      <CsvImportModal open={importOpen} onClose={() => setImportOpen(false)} onDone={load} title="Import products from CSV"
        subtitle="Add many products at once, or update existing ones by SKU." templateUrl="/vendor/products/import-template" templateLabel="Download template" fileName="products-import-template.csv"
        importUrl="/vendor/products/import" columns="name, sku, price, compare_at_price, cost_price, stock, min_stock, category, subcategory, brand, barcode, tax_rate, weight, status, featured, images, description"
        hint="Required: name, price and a unique SKU. A row whose SKU already exists updates that product (blank cells are left unchanged); other rows create new products. Separate several image links with “|”."
        summary={(r) => [{ count: r.created, label: "new" }, { count: r.updated, label: "to update" }, ...(r.failed ? [{ count: r.failed, label: "with errors", tone: "bad" }] : [])]} />

      <DataTable testId="products-table" searchTestId="products-search" exportName="products" columns={columns} data={items} loading={loading}
        searchKeys={["name", "sku", "brand", "category"]} searchPlaceholder="Search name, SKU, brand…" filters={filters}
        defaultSort={{ key: "name", dir: "asc" }} rowTestId={(p) => `product-card-${p.id}`} onRowClick={openEdit}
        emptyIcon={Package} emptyTitle="No products yet" emptyText="Add your first product to start selling." />

      <Modal open={open} onClose={() => setOpen(false)} size="xl" testId="product-modal" title={editId ? "Edit product" : "Add product"}
        subtitle="Fields marked * are required."
        footer={<>
          <button onClick={() => setOpen(false)} className={btnOutline}>Cancel</button>
          <button onClick={save} disabled={saving} data-testid="product-save" className={btnPrimary}>{saving ? "Saving…" : editId ? "Save changes" : "Add product"}</button>
        </>}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-6 gap-y-3">
          {/* Left: images + description */}
          <div className="lg:col-span-5 space-y-3">
            <div>
              <div className="text-[12px] font-semibold text-slate-700 mb-1">Product images</div>
              <ImageGallery value={v.images} onChange={(imgs) => set("images", imgs)} max={8} compact />
            </div>
            <Field label="Description" type="textarea" rows={3} maxLength={500} t="product-description" placeholder="Describe the product…" {...bind("description")} />
          </div>

          {/* Right: fields in dense rows */}
          <div className="lg:col-span-7 grid grid-cols-6 gap-x-3 gap-y-3 content-start">
            <Field className="col-span-6" label="Product name" required t="product-name" maxLength={120} placeholder="e.g. Trailblazer Water Bottle" {...bind("name")} />
            <Field className="col-span-3" label="Category" required t="product-category" list="cat-list" placeholder="e.g. Accessories" {...bind("category")}>
              <datalist id="cat-list">{categoryOptions.map((c) => <option key={c} value={c} />)}</datalist>
            </Field>
            <Field className="col-span-3" label="Subcategory" t="product-subcategory" {...bind("subcategory")} />
            <Field className="col-span-2" label="Price" required={!hasVar} type="number" min="0" step="0.01" prefix="₹" t="product-price" disabled={hasVar}
              {...(hasVar ? { value: derivedPrice, onChange: () => {}, hint: "Lowest variant price" } : bind("price"))} />
            <Field className="col-span-2" label="Compare at" type="number" min="0" step="0.01" prefix="₹" t="product-compare" disabled={hasVar} hint={hasVar ? "Set per variant" : "Crossed-out price"} {...bind("compare_at_price")} />
            <Field className="col-span-2" label="Cost price" type="number" min="0" step="0.01" prefix="₹" t="product-cost" {...bind("cost_price")} />
            <Field className="col-span-2" label="Stock" required={!hasVar} type="number" min="0" step="1" t="product-stock" disabled={hasVar}
              {...(hasVar ? { value: derivedStock, onChange: () => {}, hint: "Total of all variants" } : bind("stock"))} />
            <Field className="col-span-2" label="Low-stock alert" type="number" min="0" step="1" t="product-minstock" {...bind("min_stock")} />
            <div className="col-span-6 rounded-lg border border-slate-200 bg-slate-50/60 p-3" data-testid="product-tax-block">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className="text-[12px] font-semibold text-slate-700">Tax</span>
                {taxRule && <span className="text-[12px] px-2 py-0.5 rounded-md border bg-blue-50 text-blue-700 border-blue-200 font-semibold" data-testid="product-tax-rule">{taxRule.type} {taxRule.rate}% <span className="font-normal text-slate-500">from category “{taxRule.category}”</span></span>}
              </div>
              {isBlank(v.category) && <p className="mt-1.5 text-[12px] text-slate-500" data-testid="product-tax-hint">Choose a category — its tax (GST / VAT and %) is taken from Setup → Tax.</p>}
              {!isBlank(v.category) && !taxRule && (
                <p role="alert" className="mt-1.5 text-[12px] text-red-600" data-testid="product-tax-missing">
                  No tax is set for the category “{v.category.trim()}”. Set it in <Link to="/vendor/homepage" className="underline font-semibold">Setup → Tax</Link> first (add the category on the Categories page if it&apos;s new), then come back — this product can&apos;t be saved until then.
                </p>
              )}
              {taxRule && (
                <div className="mt-2 flex gap-2 flex-wrap" role="radiogroup" aria-label="Tax inclusive or exclusive" data-testid="product-tax-mode">
                  {[[false, "Price excludes tax", "Tax is added on top at checkout"], [true, "Price includes tax", "Tax is already inside the price"]].map(([val, label, hint]) => (
                    <label key={String(val)} className={`flex-1 min-w-[180px] rounded-lg border px-3 py-2 cursor-pointer ${!!v.tax_inclusive === val ? "border-blue-500 bg-blue-50" : "border-slate-200 bg-white"}`}>
                      <span className="flex items-center gap-2 text-[13px] font-semibold"><input type="radio" name="tax-mode" checked={!!v.tax_inclusive === val} onChange={() => set("tax_inclusive", val)} data-testid={val ? "product-tax-inclusive" : "product-tax-exclusive"} />{label}</span>
                      <span className="block text-[11px] text-slate-500 ml-6">{hint}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <Field className="col-span-2" label="SKU" t="product-sku" placeholder="Unique code" {...bind("sku")} />
            <Field className="col-span-2" label="Barcode" t="product-barcode" {...bind("barcode")} />
            <Field className="col-span-2" label="Brand" t="product-brand" {...bind("brand")} />
            <Field className="col-span-2" label="Weight" type="number" min="0" step="0.01" suffix="kg" t="product-weight" {...bind("weight")} />
            <Field className="col-span-2" label="Status" type="select" options={STATUS_OPTS} t="product-status" {...bind("status")} />
            <div className="col-span-2 flex items-end pb-1.5">
              <CheckField label="Feature on storefront" checked={v.featured} onChange={(c) => set("featured", c)} t="product-featured" />
            </div>
          </div>

          <div className="lg:col-span-12 border-t border-slate-100 pt-3">
            {!hasVar && v.variants?.length > 0 && (
              <div className="mb-3 rounded-lg border border-orange-200 bg-orange-50 px-3 py-2 text-[12px] text-orange-800 flex items-center justify-between gap-3 flex-wrap" data-testid="legacy-variants-note">
                <span>This product uses the old options ({v.variants.map((x) => `${x.name}: ${x.options.join(", ")}`).join(" · ")}) which can't have their own price or stock.</span>
                <button type="button" onClick={() => { const c = fromLegacy(v); set("option_types", c.types); set("variant_rows", c.rows); set("variants", []); toast.info("Converted. Set the price and stock of each variant, then save."); }} data-testid="legacy-convert" className="font-semibold underline whitespace-nowrap">Convert to variants</button>
              </div>
            )}
            <VariantsEditor types={v.option_types} rows={v.variant_rows} images={v.images} basePrice={v.price} showErrors={!!bind("variants").error}
              onChange={({ types, rows }) => { set("option_types", types); set("variant_rows", rows); }} />
          </div>

          <div className="lg:col-span-12 border-t border-slate-100 pt-3">
            <AttributesEditor rows={v.attributes} onChange={(rows) => set("attributes", rows)} products={items} showErrors={!!bind("attributes").error} />
          </div>
        </div>
      </Modal>
    </div>
  );
}
